import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { FormArray, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { Location } from '@angular/common';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { SharedDataService } from 'src/app/services/app/shared-service/shared-data.service';
import { CustomersService } from 'src/app/services/core/quickbook/customers/customers.service';
import { CredentialService } from 'src/app/services/core/credential/credential.service';
import { FundingSourceService } from 'src/app/services/core/funding-source/funding-source.service';
import { sweetAlert } from 'src/app/utils/swal';
import { allowOnlyNumbersValidator, phoneNumberLengthValidator } from 'src/app/utils/utils.common';
import { addressValidator } from 'src/app/utils/custom-validators';

@UntilDestroy()

@Component({
  selector: 'app-add-customer',
  templateUrl: './add-customer.component.html',
  styleUrls: ['./add-customer.component.css']
})
export class AddCustomerComponent implements OnInit {
  // Edit Modal Inputs and Outputs
  @Input() customer:any;
  @Input() source:any;
  @Output() onSaveCustomer = new EventEmitter<string>();
  @Output() onCloseModal = new EventEmitter<string>();
  // Edit Modal Inputs and Outputs End

  customerForm!: FormGroup

  customerId: any;
  quickBookId: any;
  companyId: any;
  companies: any;
  selectedCompany: any = '';
  selectedFS: any = '';
  syncToken: any;
  submitted = false;
  fundingSources:any


  constructor(
    private fundingSourceService : FundingSourceService,
    private credentialService: CredentialService,
    private activatedRoute: ActivatedRoute,
    private router: Router,
    private customerService: CustomersService,
    private fb: FormBuilder
  ) { }

  ngOnInit(): void {

    this.getCredentialCompanies();
    this.buildForm();
    this.getFundingSources()

    if (this.source === 'modal' && this.customer) {
      this.customerId = this.customer.id;
      this.getCustomerbyId();
    } else {
      this.activatedRoute.params
        .pipe(untilDestroyed(this)).subscribe((params) => {
          this.customerId = params["id"];
          if (this.customerId) {
            this.getCustomerbyId();
          }
        }
      );
    }
  }

  get form() {
    return this.customerForm.controls;
  }

  get primaryEmailAddr() {
    return this.customerForm.get('PrimaryEmailAddr') as FormGroup;
  }

  get primaryPhone() {
    return this.customerForm.get('PrimaryPhone') as FormGroup;
  }

  get billAddr() {
    return this.customerForm.get('BillAddr') as FormGroup;
  }

  get reference() {
    return this.customerForm.get('reference') as FormGroup;
  }

  get customField(): FormArray {
    return this.customerForm.get('CustomField') as FormArray;
  }

  buildForm() {
    this.customerForm = this.fb.group({
      PrimaryEmailAddr: this.fb.group({
        Address: ['', Validators.required],
      }),
      DisplayName: ['', Validators.required],
      Notes: ['', Validators.required],
      PrimaryPhone: this.fb.group({
        FreeFormNumber: ['', [Validators.required,phoneNumberLengthValidator]],

      },{ validators: allowOnlyNumbersValidator('FreeFormNumber') }),
      BillAddr: this.fb.group({
        Line1: ['', Validators.required],
        CountrySubDivisionCode: ['', Validators.required],
        City: ['', Validators.required],
        PostalCode: ['', Validators.required],
        Country: ['', Validators.required],
        coords : [[]],
      },{ validators: addressValidator('Line1') }),
      reference: this.fb.group({
        name: [''],
        id: [''],
      })
      // CustomField: this.fb.array([]),
    });
  }

  getCustomerbyId() {
    this.customerService.getCustomersById(this.customerId).pipe(untilDestroyed(this)).subscribe((data: any) => {
      if (data) {
        this.customerForm.patchValue({
          ...data
        })
        
        if(data.reference)
          this.selectedFS = data.reference.id;
        
        this.syncToken = data.SyncToken;
        this.quickBookId = data.Id;
      }
    })
  }

  onSave() {
    this.submitted = true;
    if (this.customerForm.invalid) {
      return;
    }
    const payload = {
      ...this.customerForm.value,
    }

    if (this.customerId) {
      payload.SyncToken = this.syncToken,
        payload.Id = this.quickBookId
    }
    if (payload?.BillAddr) {
      delete payload.BillAddr.coords;
    }

    this.customerService.saveCustomers(this.customerId, payload).pipe(untilDestroyed(this)).subscribe((data: any) => {
      if (data) {
        if (this.source === 'modal') {
          this.onSaveCustomer.emit(data);
        } else {
          this.router.navigateByUrl('/quickbook/customers')
        }
      }
    }, (err) => {

        sweetAlert("Error", err, "error", "Ok");
      })
  }

  getCredentialCompanies() {
    const queryparams = {
      limit: 9999,
      isActive: true
    }
    this.credentialService.getCompanies(queryparams)
      .pipe(untilDestroyed(this)).subscribe((data: any) => {
        if (data && data.results) {
          this.companies = data.results
        }
      })
  }

   getFundingSources() {
    let queryparams: any = {
      limit: 9999,
      isActive: true
    }
    this.fundingSourceService
      .get(queryparams)
      .pipe(untilDestroyed(this)).subscribe((data: any) => {
        if (data && data.results ) {
          this.fundingSources = data.results.sort((a: any, b: any) =>
            (a.title).toLowerCase().localeCompare((b.title).toLowerCase())
          );
        }
      });
  }

  ngOnDestroy(): void {

  }

  onSelectAddress(place: any): void {

    const selectedAddress = place.address_components;
    // city
    const city = selectedAddress.find((data: any) =>
      data.types.includes('locality') || data.types.includes('administrative_area_level_2')
    );
    const cityName = city ? city.long_name : '';

    // state
    const state = selectedAddress.find((data: any) =>
      data.types.includes('administrative_area_level_1')
    );
    const stateName = state ? state.short_name : '';

    // Postal Code
    const postalCode = selectedAddress.find((data: any) =>
      data.types.includes('postal_code')
    );
    const postalCodeValue = postalCode ? postalCode.long_name : '';

    // Country
    const country = selectedAddress.find((data: any) =>
      data.types.includes('country')
    );
    const countryName = country ? country.long_name : '';

    this.customerForm.patchValue({
      BillAddr: {
        Line1: place.formatted_address,
        CountrySubDivisionCode: stateName,
        City: cityName,
        PostalCode: postalCodeValue,
        Country: countryName,
        coords: [
          place.geometry.location.lng(),
          place.geometry.location.lat()
        ]
      }
    });


  }

  onFundingSourceChange(event: Event) {
    const fs = this.fundingSources.find((c: any) => c.id === this.selectedFS);
    this.customerForm.get('reference')?.patchValue({
        name: fs.title,
        id:fs.id
    })
  }

  onChangeAddress() {
    this.customerForm.patchValue({
      BillAddr: {
        City: '',
        CountrySubDivisionCode: '',
        PostalCode: '',
        Country: '',
        coords: ''
      }
    });
  }
}
